import { Injectable } from '@angular/core';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { ApiNamesEnum, EventTypeEnum, RequestTypeEnum, ToastrActionEnum, ToastrStringsEnum } from '../utils/enums';
import { SettingsService } from './settings/settings.service';
import { EventBus } from './auth/event.bus';
import { filter } from 'rxjs/operators';
import { ApiRequestError } from '../utils/interfaces/api-request-error.interface';
import { CerberusErrorCodesEnum, HuginnErrorCodesEnum, NephilimErrorCodesEnum, WolandErrorCodesEnum } from '../utils/enums/error-codes';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class ToastrDialogService {
  private _toastrSequenceMap = {};

  private supportedRequests: RequestTypeEnum[] = [
    RequestTypeEnum.FileDownload,
    RequestTypeEnum.FileDelete,
    RequestTypeEnum.FileUpload,

    RequestTypeEnum.CorrectionList,
    RequestTypeEnum.CorrectionListAddWord,
    RequestTypeEnum.CorrectionListDeleteWord,

    RequestTypeEnum.ExamCheckAccessCode,
    RequestTypeEnum.ExamCheckUnlockCode,
    RequestTypeEnum.ExamCreate,
    RequestTypeEnum.ExamDownloadFile,
    RequestTypeEnum.ExamExtend,
    RequestTypeEnum.ExamGetById,
    RequestTypeEnum.ExamList,
    RequestTypeEnum.ExamSaveFiles,
    RequestTypeEnum.ExamSubmit,
    RequestTypeEnum.CancelExam,

    RequestTypeEnum.FeatureRestrictionTemplate,
    RequestTypeEnum.FeatureRestrictionTemplateAdd,
    RequestTypeEnum.FeatureRestrictionTemplateDelete,
    RequestTypeEnum.FeatureRestrictionTemplateList,
    RequestTypeEnum.FeatureRestrictionTemplateUpdate,

    RequestTypeEnum.WolandOneDriveRequestToken,
  ];

  constructor(
    private readonly eventBus: EventBus,
    private toastrService: ToastrService,
    private translate: TranslateService,
    private settingsSvc: SettingsService
  ) {
    this.eventBus
      .on(EventTypeEnum.HTTP_ERROR)
      .pipe(filter(e => this.supportedRequests.includes(e.data.requestType)))
      .subscribe(event => {
        const error = event.data as ApiRequestError;
        const { apiName, errorCode } = error;

        if (error.response && !error.response.ok && error.response.status === 400) {
          const statusCode = error.response.status;

          const message = this.extractValidationErrors(error.response);

          this.showToaster(ToastrActionEnum.Error, message, this.translate.instant(`HTTP_ERRORS.${statusCode.toString()}.TITLE`), true);
          return;
        }

        this.showApiErrorToastr(errorCode, apiName, error.response.status);
      });
  }

  public showToastrByType(action: ToastrActionEnum, type: ToastrStringsEnum, disableTimeOut?: boolean, key?: string): void {
    const content = this.toastrContent(type);
    this.showToaster(action, content.body, content.title, disableTimeOut, key);
  }

  public hideToastr(key: string): void {
    if (key && this._toastrSequenceMap[key]) {
      this._toastrSequenceMap[key].forEach(toastId => {
        this.toastrService.remove(toastId);
      });
      delete this._toastrSequenceMap[key];
    }
  }

  private toastrContent(type: ToastrStringsEnum): { body: string, title: string } {
    const currentCloud = this.settingsSvc.getCurrentCloud();

    return {
      body: this.getTranslation(`TOASTR.${type}.CONTENT`, { currentCloud }),
      title: this.getTranslation(`TOASTR.${type}.TITLE`, { currentCloud })
    };
  }

  public showToaster(action: ToastrActionEnum, message: string, title?: string, disableTimeOut?: boolean, key?: string) {
    if (key && this._toastrSequenceMap[key]) {
      this._toastrSequenceMap[key].forEach(toastId => {
        this.toastrService.remove(toastId);
      });
      delete this._toastrSequenceMap[key];
    }

    let toastr: ActiveToast<any> | null = null;

    switch (action) {
      case ToastrActionEnum.Success:
        toastr = this.toastrService.success(message, title, { disableTimeOut });
        break;
      case ToastrActionEnum.Info:
        toastr = this.toastrService.info(message, title, { disableTimeOut });
        break;
      case ToastrActionEnum.Error:
        toastr = this.toastrService.error(message, title, { disableTimeOut });
        break;
      case ToastrActionEnum.Warning:
        toastr = this.toastrService.warning(message, title, { disableTimeOut });
        break;
      default: break;
    }

    if (key && disableTimeOut) {
      if (!this._toastrSequenceMap[key]) {
        this._toastrSequenceMap[key] = [];
      }
      if (toastr && toastr.toastId) {
        this._toastrSequenceMap[key].push(toastr.toastId);
      }
    }
  }

  public showApiErrorToastr(errorCode: CerberusErrorCodesEnum | NephilimErrorCodesEnum | HuginnErrorCodesEnum | WolandErrorCodesEnum | null,
                            apiName: ApiNamesEnum, statusCode?: number) {
    let message = '';
    let title = '';
    let disableTimeOut = false;

    // All Unknown values equal 0 across enums, so a single value covers all APIs.
    const isUnknownError = errorCode === CerberusErrorCodesEnum.Unknown;
    const isHttpStatusError = [400, 404, 500].includes(statusCode!);

    if (isUnknownError || statusCode === 0) {
      message = 'HTTP_ERRORS.CHECK_CONNECTION';
    } else if (errorCode) {
      message = `${apiName.toString().toUpperCase()}.ERRORS.${errorCode}`;
      // Keep the toast visible until dismissed, the teacher needs to take action and should be able to read the toastr.
      disableTimeOut = errorCode === WolandErrorCodesEnum.OneDriveTokenExpired;
    } else if (isHttpStatusError) {
      title = this.translate.instant(`HTTP_ERRORS.${statusCode}.TITLE`);
      message = `HTTP_ERRORS.${statusCode}.MESSAGE`;
    }

    this.showToaster(ToastrActionEnum.Error, this.translate.instant(message), title, disableTimeOut);
  }

  private getTranslation(key: string, param?: object): string {
    return this.translate.instant(key, param);
  }

  private extractValidationErrors(response: HttpErrorResponse): string {
    let messages = '';

    if (!response.error.errors) {
      return messages;
    }

    const errors = response.error.errors;

    for (const key in errors) {
      if (Object.prototype.hasOwnProperty.call(errors, key)) {
        messages = messages + errors[key] + '\n';
      }
    }

    return messages;
  }
}
